import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { RedButton, GhostButton } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";

const CATEGORIES = ["Mécanique", "Commande de pièces", "Reprogrammation", "Électronique", "Diagnostic"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const frDate = (s) => new Date(s + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const slugify = (c) => c.toLowerCase().normalize("NFD").replace(/[^a-z]/g, "");

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.2em] text-zinc-400 uppercase">{label}</span>
    <input data-testid={testid}
      className="mt-2 w-full bg-black border border-zinc-800 px-4 py-3 text-white placeholder-zinc-600 focus:border-sared focus:outline-none"
      {...props} />
  </label>
);

export default function RendezVous() {
  const { settings } = useSite();
  const s = settings || {};
  const [step, setStep] = useState(0);
  const [cats, setCats] = useState([]);
  const [weekStart, setWeekStart] = useState(addDays(new Date(), 1));
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [pick, setPick] = useState(null);
  const [form, setForm] = useState({ last_name: "", phone: "", email: "", vehicle_brand: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [hoursText, setHoursText] = useState("");

  useEffect(() => {
    api.get("/planning/hours").then(({ data }) => setHoursText(data.text)).catch(() => {});
  }, []);

  useEffect(() => {
    if (step !== 0) return;
    let active = true;
    (async () => {
      setLoadingDays(true);
      try {
        const { data } = await api.get(`/planning/days?start=${iso(weekStart)}&count=7`);
        if (active) setDays(data.days);
      } catch (e) { toast.error(apiError(e)); }
      finally { if (active) setLoadingDays(false); }
    })();
    return () => { active = false; };
  }, [step, weekStart]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (c) => setCats(cats.includes(c) ? cats.filter((x) => x !== c) : [...cats, c]);
  const valid = cats.length > 0 && ["last_name", "phone", "email"].every((k) => form[k].trim().length > 0);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/appointments", {
        ...form, date: pick.date, start: pick.start, service_text: cats.join(" • "),
      });
      setDone(data);
      toast.success("Demande envoyée !");
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 409) setStep(0);
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="booking-success">
        <PageHero overline="Confirmation" title="Demande envoyée" />
        <section className="mx-auto max-w-3xl px-4 md:px-8 py-16">
          <div className="border border-sared bg-black p-8">
            <div className="overline mb-3">Référence</div>
            <div className="font-head text-5xl text-sared" data-testid="booking-reference">{done.reference}</div>
            <p className="mt-6 text-sm text-zinc-300 capitalize">
              {frDate(done.date)} · {done.start} – {done.end}
            </p>
            <p className="mt-4 text-sm text-zinc-400">Le garage valide votre créneau et vous recontacte.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/mon-rendez-vous"><RedButton>Suivre ma demande</RedButton></Link>
              <a href={`tel:${String(s.phone || "").replace(/\s/g, "")}`}>
                <GhostButton><Phone size={14} className="inline mr-2" />{s.phone}</GhostButton>
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="rendez-vous-page">
      <PageHero overline="Réservation en ligne" title="Demander un rendez-vous" />

      <div className="mx-auto max-w-5xl px-4 md:px-8 pt-8">
        <div className="border-l-2 border-sared pl-4" data-testid="rdv-hours">
          <div className="font-mono-tech text-[10px] tracking-[0.2em] text-sared">HORAIRES</div>
          <p className="mt-1 text-sm text-zinc-300 whitespace-pre-line">{hoursText || s.hours}</p>
        </div>
      </div>

      <section className="mx-auto max-w-5xl px-4 md:px-8 py-12">
        <div className="flex gap-2 mb-8" data-testid="booking-stepper">
          {["Créneau", "Coordonnées"].map((label, i) => (
            <div key={label} className={`flex items-center gap-2 px-3 py-2 border font-mono-tech text-[10px] tracking-[0.16em] uppercase ${
              i === step ? "border-sared text-sared bg-sared/10" : i < step ? "border-zinc-700 text-zinc-400" : "border-zinc-900 text-zinc-700"}`}>
              {i < step ? <Check size={12} /> : <span>{i + 1}</span>} {label}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="border border-zinc-800 bg-[#0a0a0a] p-5 md:p-8" data-testid="step-slot">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-base md:text-lg text-white">Créneaux libres</h2>
              <div className="flex gap-2">
                <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="slots-prev"
                        className="border border-zinc-800 p-2 text-zinc-300 hover:border-sared"><ChevronLeft size={16} /></button>
                <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="slots-next"
                        className="border border-zinc-800 p-2 text-zinc-300 hover:border-sared"><ChevronRight size={16} /></button>
              </div>
            </div>
            {loadingDays ? <p className="text-zinc-500">Chargement...</p> : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {days.map((d) => (
                  <div key={d.date} className="border border-zinc-900 p-4" data-testid={`slots-day-${d.date}`}>
                    <div className="font-head text-lg text-white capitalize">{frDate(d.date)}</div>
                    {!d.open && <div className="mt-2 font-mono-tech text-[9px] tracking-[0.16em] text-zinc-500">{d.reason || "FERMÉ"}</div>}
                    <div className="mt-3 grid gap-2">
                      {d.slots.filter((x) => x.state === "free").map((x) => (
                        <button key={x.start} onClick={() => { setPick({ date: d.date, start: x.start, end: x.end }); setStep(1); }}
                                data-testid={`slot-${d.date}-${x.start}`}
                                className={`border px-3 py-2 text-left font-mono-tech text-[11px] tracking-[0.14em] transition-colors ${
                                  pick && pick.date === d.date && pick.start === x.start
                                    ? "bg-sared border-sared !text-white"
                                    : "border-emerald-600/60 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`}>
                          {x.start} – {x.end}
                        </button>
                      ))}
                      {d.open && d.slots.filter((x) => x.state === "free").length === 0 && (
                        <p className="text-xs text-zinc-600">Complet</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-8">
              <RedButton disabled={!pick} onClick={() => setStep(1)} data-testid="slot-next"
                         className={!pick ? "opacity-40 cursor-not-allowed" : ""}>
                {pick ? `Continuer — ${pick.start}` : "Choisissez un créneau"}
              </RedButton>
            </div>          </div>
        )}

        {step === 1 && (
          <div className="border border-zinc-800 bg-[#0a0a0a] p-7 md:p-10" data-testid="step-contact">
            <div className="font-mono-tech text-[10px] tracking-[0.2em] text-sared" data-testid="contact-slot-recap">
              {frDate(pick.date).toUpperCase()} · {pick.start} – {pick.end}
            </div>

            <h2 className="mt-8 text-base md:text-lg text-white">Prestation</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const active = cats.includes(c);
                return (
                  <button key={c} onClick={() => toggle(c)} data-testid={`pick-category-${slugify(c)}`} aria-pressed={active}
                          className={`px-4 py-2 border font-mono-tech text-[11px] tracking-[0.14em] uppercase transition-colors ${
                            active ? "border-sared bg-sared !text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                    {c}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <Field label="Nom" testid="input-lastname" value={form.last_name} onChange={set("last_name")} />
              <Field label="Téléphone" testid="input-phone" value={form.phone} onChange={set("phone")} />
              <Field label="E-mail" testid="input-email" type="email" value={form.email} onChange={set("email")} />
              <Field label="Véhicule (optionnel)" testid="input-message" value={form.vehicle_brand} onChange={set("vehicle_brand")} />
            </div>

            <div className="mt-10 flex gap-4">
              <GhostButton onClick={() => setStep(0)} data-testid="contact-back">Retour</GhostButton>
              <RedButton onClick={submit} disabled={!valid || sending} data-testid="confirm-appointment"
                         className={!valid ? "opacity-40 cursor-not-allowed" : ""}>
                {sending ? "Envoi..." : "Confirmer"}
              </RedButton>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
